import { useState, type FC } from 'react'

import { MessagePopup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Input } from '@skytech/ui/Input'

const DEFAULT_DELAY_SECONDS = 900 // 15 minutes

type Props = {
  isOpen: boolean
  /** Label shown in the modal title to identify which workflow is being started. */
  workflowLabel?: string
  /** Called when the user confirms acceleration with the chosen delay in seconds. */
  onConfirm: (delaySeconds: number) => void
  /** Called when the user skips acceleration and wants normal workflow timing. */
  onSkip: () => void
}

/**
 * Dev-only modal that lets testers configure accelerated timing before starting a workflow.
 *
 * Drop this in before any workflow start action guarded by `isDev`:
 *
 * ```tsx
 * {isDev && (
 *   <WorkflowAccelerateModal
 *     isOpen={showAccelerateModal}
 *     workflowLabel="Offboarding"
 *     onConfirm={seconds => submitWithAcceleration(seconds)}
 *     onSkip={() => submitNormally()}
 *   />
 * )}
 * ```
 */
export const WorkflowAccelerateModal: FC<Props> = ({
  isOpen,
  workflowLabel,
  onConfirm,
  onSkip,
}) => {
  const [delaySeconds, setDelaySeconds] = useState(DEFAULT_DELAY_SECONDS)

  const title = workflowLabel
    ? `[DEV] Accelerate ${workflowLabel} Workflow?`
    : '[DEV] Accelerate Workflow?'

  const handleConfirm = () => {
    const validated = Math.max(1, delaySeconds)
    onConfirm(validated)
  }

  const handleDelayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10)
    setDelaySeconds(isNaN(value) ? DEFAULT_DELAY_SECONDS : value)
  }

  return (
    <MessagePopup
      type="warning"
      title={title}
      message="Run the workflow in accelerated mode? Each wait between steps will use the delay below instead of real calendar days."
      isOpen={isOpen}
    >
      <div className="mt-4 flex flex-col gap-4">
        <Input
          id="accelerated-delay-seconds"
          label="Delay between steps (seconds)"
          type="number"
          inputMode="numeric"
          min="1"
          value={delaySeconds}
          onChange={handleDelayChange}
          isFullWidth
        />
        <p className="text-sm text-zinc-500">
          Default: 900 s (15 min). Lower values make cancel-window testing easier.
        </p>
      </div>

      <footer className="mt-4 flex justify-end gap-2">
        <Button variant="tertiary" onClick={onSkip}>
          Skip — use normal timing
        </Button>
        <Button variant="primary" onClick={handleConfirm}>
          Start accelerated
        </Button>
      </footer>
    </MessagePopup>
  )
}
